import React, { useMemo } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';

import { RegisterDetailProps } from './register-types';

import {
  RegisterItemDetail,
  RegisterSection,
  RegisterSectionField,
} from './register-section';

import {
  constructDatingString,
  constructOriginatorEventLabel,
  constructRelationLink,
} from './utils/construct-originator-labels';
import { registerKeyLabels } from './register-enums';

import RegisterDetailBodySection from './register-detail-body-section';
import LoaderOverlay from 'components/loader/loader-overlay';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(3),
      //paddingTop: 0,
      minHeight: '100px',
      borderTop: '1px solid rgba(224, 224, 224, 1)',
      display: 'flex',
      flexWrap: 'nowrap',
    },
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
    bracketSection: {
      display: 'flex',
      alignItems: 'stretch',
      marginBottom: 16,
    },
    bracket: {
      borderLeft: '2px solid rgba(224, 224, 224, 1)',
      borderBottom: '2px solid rgba(224, 224, 224, 1)',
      paddingLeft: 8,
      width: 16,
      height: 'auto',
    },
    chip: {
      textTransform: 'uppercase',
      background: 'rgba(0,0,0,0.1)',
      padding: '4px 8px',
    },
    title: {
      // fontSize: theme.typography.h4,
    },
  })
);

export function RegisterDetail({ detail }: RegisterDetailProps) {
  return () => RegisterDetailComponent({ detail });
}

export function RegisterDetailComponent({ detail }: RegisterDetailProps) {
  const classes = useStyles();
  const {
    preferredName,
    originatorClassLabel,
    subClassLabel,
    nameVariants,
    body,
    originDating,
    endingDating,
    events,
    identifiers,
  } = detail ?? {};

  const originDateRelations = useMemo(
    () => detail?.relations?.filter?.((r) => r?.targetPart === 'ORIGIN_DATING'),
    [detail?.relations]
  );

  const endingDateRelations = useMemo(
    () =>
      (detail?.relations ?? []).filter(
        (r) => r?.targetPart === 'ENDING_DATING'
      ),
    [detail?.relations]
  );

  const eventRelations = useMemo(
    () => (detail?.relations ?? []).filter((r) => r?.targetPart === 'EVENT'),
    [detail?.relations]
  );

  const bodyRelations = useMemo(
    () => (detail?.relations ?? []).filter((r) => r?.targetPart === 'BODY'),
    [detail?.relations]
  );

  const otherRelations = useMemo(
    () =>
      (detail?.relations ?? [])
        .filter(
          (r) =>
            r?.targetPart !== 'ORIGIN_DATING' &&
            r?.targetPart !== 'ENDING_DATING' &&
            r?.targetPart !== 'EVENT' &&
            r?.targetPart !== 'BODY'
        )
        .filter((r) => r?.relatedEntity?.resolved),
    [detail?.relations]
  );

  if (!detail) {
    return <LoaderOverlay />;
  }

  return (
    <Grid container className={classes.root}>
      <Grid item style={{ width: 0 }} />
      <Grid container>
        <Grid item xs={12}>
          <div style={{ margin: '16px 0' }}>
            <h2>{detail.label}</h2>
            <span style={{ fontWeight: 400 }}>
              {body?.briefDescription ?? ''}
            </span>
          </div>
          <div className={classes.chip}>
            {originatorClassLabel} {'>'} {subClassLabel}
          </div>
        </Grid>

        <RegisterSection title="Označení">
          <div style={{ paddingBottom: 16 }}>
            <RegisterSectionField value={preferredName && detail.label}>
              {preferredName && (
                <RegisterItemDetail
                  item={preferredName}
                  keyLabels={registerKeyLabels.name}
                />
              )}
            </RegisterSectionField>
            <div style={{ marginTop: 8 }}>
              {nameVariants?.map((nv) => (
                <div key={nv.id} className={classes.bracketSection}>
                  <div className={classes.bracket} />
                  <div>
                    <RegisterSectionField value={nv.label}>
                      <RegisterItemDetail
                        item={nv}
                        keyLabels={registerKeyLabels.name}
                      />
                    </RegisterSectionField>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </RegisterSection>

        {originDating && (
          <RegisterSection title="Vznik">
            <RegisterSectionField value={constructDatingString(originDating)}>
              <RegisterItemDetail
                item={originDating}
                keyLabels={registerKeyLabels.origin}
              />
            </RegisterSectionField>

            <div style={{ paddingLeft: 8, paddingBottom: 16 }}>
              {(originDateRelations ?? [])
                .filter((r) => r.relatedEntity.resolved)
                .map((r, index) => (
                  <RegisterSectionField
                    key={`rsection-vznik-${index}`}
                    label={r.typeLabel}
                    value={r.relatedEntity.label}
                    directLink={constructRelationLink(r.relatedEntity)}
                  />
                ))}
            </div>
          </RegisterSection>
        )}
        {endingDating && (
          <RegisterSection title="Zánik">
            <RegisterSectionField value={constructDatingString(endingDating)}>
              <RegisterItemDetail
                item={endingDating}
                keyLabels={registerKeyLabels.ending}
              />
            </RegisterSectionField>
            <div style={{ paddingLeft: 8, paddingBottom: 16 }}>
              {(endingDateRelations ?? [])
                .filter((r) => r.relatedEntity.resolved)
                .map((r, index) => (
                  <RegisterSectionField
                    key={`rsection-zanik-${index}`}
                    label={r.typeLabel}
                    value={r.relatedEntity.label}
                    directLink={constructRelationLink(r.relatedEntity)}
                  />
                ))}
            </div>
          </RegisterSection>
        )}

        <RegisterDetailBodySection
          body={body}
          bodyRelations={bodyRelations}
          originatorDetail={detail}
        />

        {(events ?? []).length > 0 && (
          <RegisterSection title="Událost">
            <div style={{ paddingLeft: 8, paddingBottom: 16 }}>
              {(events ?? []).map((e, index) => (
                <div className={classes.bracketSection}>
                  <div className={classes.bracket} />
                  <div>
                    <RegisterSectionField
                      key={e.id}
                      label={e.typeLabel}
                      value={constructOriginatorEventLabel(e)}
                    >
                      <RegisterItemDetail
                        item={e}
                        keyLabels={registerKeyLabels.event}
                      />
                    </RegisterSectionField>
                    <div style={{ paddingLeft: 8 }}>
                      {(eventRelations ?? [])
                        .filter((r) => r.relatedEntity.resolved)
                        .filter((r) => r.targetPartIndex === index)
                        .map((r) => (
                          <RegisterSectionField
                            label={r.typeLabel}
                            value={r.relatedEntity.label}
                            directLink={constructRelationLink(r.relatedEntity)}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </RegisterSection>
        )}
        <RegisterSection title="Vztah">
          <div style={{ paddingLeft: 8, paddingBottom: 16 }}>
            {(otherRelations ?? []).length > 0 ? (
              <>
                {(otherRelations ?? [])
                  .filter((r) => r.relatedEntity.resolved)
                  .map((r, index) => (
                    <div
                      key={`rsection-other-${index}`}
                      className={classes.bracketSection}
                    >
                      <div>
                        <RegisterSectionField
                          label={r.typeLabel}
                          value={r.relatedEntity.label}
                          directLink={constructRelationLink(r.relatedEntity)}
                        />
                      </div>
                    </div>
                  ))}
              </>
            ) : (
              <span style={{ fontWeight: 400 }}>
                Sekce neobsahuje žádné informace
              </span>
            )}
          </div>
        </RegisterSection>
        {identifiers && identifiers?.length > 0 && (
          <RegisterSection title="Identifikátor">
            <div style={{ paddingLeft: 8, paddingBottom: 16 }}>
              {identifiers?.map((idn) => (
                <div
                  key={`${idn.typeLabel}: ${idn.value}`}
                  className={classes.bracketSection}
                >
                  <div className={classes.bracket} />
                  <div>
                    <RegisterSectionField
                      value={`${idn.typeLabel}: ${idn.value}`}
                    >
                      <RegisterItemDetail
                        item={idn}
                        keyLabels={registerKeyLabels.identificators}
                      />
                    </RegisterSectionField>
                  </div>
                </div>
              ))}
            </div>
          </RegisterSection>
        )}
      </Grid>
    </Grid>
  );
}
